import {
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  effect,
  ElementRef,
  inject,
  input,
  output,
  resource,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FilterEventService } from '@services/filter-event.service';
import { SdDrawer } from '@solid-design-system/components/dist-versioned';
import { firstValueFrom } from 'rxjs';
import { DashboardService, Filter, FilterTemplate, FilterType, FilterValue } from 'src/openapi-client';
import { FilterTemplateService } from 'src/openapi-client';
import { NotificationUtils } from 'src/app/common/util/notification';
import {
  FilterTemplateResult,
  SaveFilterTemplateDialogComponent,
} from './save-filter-template-dialog/save-filter-template-dialog.component';
import { RenameFilterTemplateDialogComponent } from './rename-filter-template-dialog/rename-filter-template-dialog.component';
import { DeleteFilterTemplateDialogComponent } from './delete-filter-template-dialog/delete-filter-template-dialog.component';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import iconLibrary from '../../../common/iconLibrary';
import { FilterComponent, Type } from 'src/app/common/components/filter/filter.component';

import { VALUE_DISPLAY_FILTER_KEY } from 'src/app/common/services/value-display.service';

@Component({
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  selector: 'app-filter-sidepanel',
  standalone: true,
  imports: [
    SaveFilterTemplateDialogComponent,
    RenameFilterTemplateDialogComponent,
    DeleteFilterTemplateDialogComponent,
    TranslocoModule,
    FilterComponent,
  ],
  templateUrl: './filter-sidepanel.component.html',
  styleUrl: './filter-sidepanel.component.scss',
})
export class FilterSidepanelComponent {
  readonly FUNDS_FILTER_KEY = 'funds';
  //TODO extract filter logic to a service

  private readonly filterEventService = inject(FilterEventService);

  readonly dashboardId = input.required<string>();
  readonly activeTabName = input<string | undefined>(undefined);

  readonly title = 'dashboard.sidepanel.title';
  readonly filterTemplates = 'dashboard.sidepanel.filterTemplates';
  readonly filterTemplatesMenu = 'dashboard.sidepanel.filterTemplatesMenu';
  readonly noTemplate = 'dashboard.sidepanel.noTemplate';
  readonly newTemplate = 'dashboard.sidepanel.newTemplate';
  readonly deleteTemplate = 'dashboard.sidepanel.deleteTemplate.menu';
  readonly applyChanges = 'dashboard.sidepanel.applyChanges';
  readonly discardChanges = 'dashboard.sidepanel.discardChanges';
  readonly changesSaved = 'dashboard.sidepanel.changesSaved';
  readonly renameTemplate = 'dashboard.renameTemplate.menu';
  readonly defaultTemplateToggle = 'dashboard.sidepanel.defaultTemplateToggle';
  readonly defaultTemplateToggleTitle = 'dashboard.saveTemplate.dialog.autoLoadTitle';
  readonly defaultTemplateSaved = 'dashboard.sidepanel.defaultTemplateSaved';
  readonly resetFilters = 'dashboard.sidepanel.resetFilters';
  readonly noFiltersAvailable = 'dashboard.sidepanel.noFiltersAvailable';
  readonly filterLoadError = 'dashboard.sidepanel.filterLoadError';
  readonly templateSaved = 'dashboard.sidepanel.templateSaved';
  readonly templateSaveError = 'dashboard.sidepanel.templateSaveError';
  readonly templateDeleteError = 'dashboard.sidepanel.templateDeleteError';
  readonly FilterType = Type;

  private readonly selection = signal<Map<string, string[]>>(new Map());

  private readonly dashboardService = inject(DashboardService);

  readonly selectionChange = output<FilterValue[]>();

  @ViewChild('drawer') private readonly drawer!: ElementRef<SdDrawer>;

  @ViewChild(SaveFilterTemplateDialogComponent) private readonly dialog!: SaveFilterTemplateDialogComponent;

  @ViewChild(DeleteFilterTemplateDialogComponent) private readonly deleteDialog!: DeleteFilterTemplateDialogComponent;

  private readonly filterTemplateService = inject(FilterTemplateService);
  private readonly translocoService = inject(TranslocoService);

  @ViewChild(RenameFilterTemplateDialogComponent) private readonly renameDialog!: RenameFilterTemplateDialogComponent;

  private static readonly MOCK_FILTERS: Filter[] = [] satisfies Filter[];

  readonly filters = resource<Filter[], { dashboardId: string; tabId: string | undefined }>({
    params: () => ({ dashboardId: this.dashboardId(), tabId: this.activeTabName() }),
    loader: async ({ params }) => ((await firstValueFrom(this.dashboardService.loadTabsFilter(params.dashboardId, params.tabId!))).filters ?? [])
  });

  /** All filter templates of the current user (newest first). */
  readonly userfilterTemplates = resource({
    params: () => ({
      documentId: this.dashboardId(),
    }),
    loader: async () => {
      return await firstValueFrom(this.filterTemplateService.loadFilterTemplates());
    },
  });

  protected readonly selectedTemplateId = signal<string | undefined>(undefined);

  private readonly autoLoadApplied = signal<boolean>(false);

  /** Snapshot of the selected template's saved filters, used to restore on discard. */
  private readonly baseline = signal<Map<string, string[]>>(new Map());

  /** Whether a template is currently selected (edit mode). */
  readonly isTemplateSelected = computed(() => {
    const id = this.selectedTemplateId();
    return id !== null && id !== undefined && id !== '';
  });

  /** Whether the current selection differs from the saved template. */
  readonly isDirty = computed(() => {
    if (!this.isTemplateSelected()) {
      return false;
    }
    return !FilterSidepanelComponent.selectionEquals(this.baseline(), this.selection());
  });

  /** Localized footer save/apply button label. */
  readonly saveLabel = computed(() => (this.isTemplateSelected() ? this.applyChanges : this.newTemplate));

  /** Localized footer secondary button label. */
  readonly secondaryLabel = computed(() => (this.isDirty() ? this.discardChanges : this.resetFilters));

  readonly hasActiveFilters = computed(() => {
    for (const values of this.selection().values()) {
      if (values.length > 0) {
        return true;
      }
    }
    return false;
  });

  readonly activeFilters = computed<FilterValue[]>(() => this.toFilterValues(this.selection()));

  readonly selectedTemplateName = computed(() => {
    const id = this.selectedTemplateId();
    if (id === null) {
      return '';
    }
    const found = (this.userfilterTemplates.value() ?? []).find(template => template.id === id);
    return found?.name ?? '';
  });

  readonly selectedTemplateAutoLoad = computed(() => {
    const id = this.selectedTemplateId();
    const found = (this.userfilterTemplates.value() ?? []).find(template => template.id === id);
    return found?.autoLoad ?? false;
  });

  constructor() {
    effect(() => {
      this.selection();
      this.selectionChange.emit(this.activeFilters());
    });

    this.filterEventService.selectionEvent.pipe(takeUntilDestroyed()).subscribe(event => {
      const selection = this.filters.value()?.find(matchedFilter => event[matchedFilter.dataLink!]);
      if (selection) {
        event[selection.dataLink!].forEach(option => this.toggleOption(selection.key, option));
      }
    });

    this.filterEventService.selectionEvent.pipe(takeUntilDestroyed()).subscribe(event => {
      const selection = this.filters.value()?.find(matchedFilter => event[matchedFilter.dataLink!]);
      if (selection) {
        event[selection.dataLink!].forEach(option => this.toggleOption(selection.key, option));
      }
    });
  }

  private static selectionEquals(a: Map<string, string[]>, b: Map<string, string[]>): boolean {
    if (a.size !== b.size) {
      return false;
    }
    for (const key of a.keys()) {
      const av = a.get(key) ?? [];
      const bv = b.get(key) ?? [];
      if (av.length !== bv.length) {
        return false;
      }
      for (const option of av) {
        if (!bv.includes(option)) {
          return false;
        }
      }
    }
    return true;
  }

  public open(): void {
    this.applyAutoLoadIfNeeded();
    this.drawer?.nativeElement.show();
  }

  public close(): void {
    this.drawer?.nativeElement.hide();
  }

  public openSaveTemplateDialog(): void {
    const currentName = `Vorlage ${this.nextTemplateName()}`;
    const component = this.dialog;
    if (!component) {
      return;
    }
    component.name.set(currentName);
    component.autoLoad.set(false);
    component.open();
  }

  public openRenameTemplateDialog(): void {
    const component = this.renameDialog;
    if (!component) {
      return;
    }
    component.open(this.selectedTemplateName());
  }

  protected existingNamesForRename(): string[] {
    return (this.userfilterTemplates.value() ?? [])
      .filter(template => template.id !== this.selectedTemplateId())
      .map(template => template.name);
  }

  public onTemplateRenamed(newName: string): void {
    const id = this.selectedTemplateId();
    if (!id) {
      return;
    }
    void firstValueFrom(this.filterTemplateService.renameFilterTemplate(id, { name: newName, filterValues: [] }))
      .then(() => {
        this.selectedTemplateId.set(undefined);
        this.refreshTemplates(newName, false, 'dashboard.renameTemplate.toast.success');
      })
      .catch(error =>
        NotificationUtils.displayNotification(
          this.translocoService.translate(this.templateSaveError, {
            errorDetail: error?.message ?? '',
          }),
          'error',
          { closable: true, durationInMs: 5000 }
        )
      );
  }

  public onTemplateToggleChange(): void {
    const id = this.selectedTemplateId();
    if (!id) {
      return;
    }
    const template = (this.userfilterTemplates.value() ?? []).find(t => t.id === id);
    if (!template) {
      return;
    }
    const autoLoad = !template.autoLoad;
    void firstValueFrom(
      this.filterTemplateService.saveFilterTemplate({
        id,
        name: template.name,
        documentId: this.dashboardId(),
        autoLoad,
        filterValues: [],
      })
    )
      .then(() => {
        this.baseline.set(this.selection());
        this.refreshTemplates(template.name, autoLoad, autoLoad
          ? 'dashboard.sidepanel.defaultTemplateSaved'
          : 'dashboard.sidepanel.defaultTemplateCleared');
      })
      .catch(error =>
        NotificationUtils.displayNotification(
          this.translocoService.translate(this.templateSaveError, {
            errorDetail: error?.message ?? '',
          }),
          'error',
          { closable: true, durationInMs: 5000 }
        )
      );
  }

  public onTemplateSaved(event: FilterTemplateResult): void {
    if (!event || !event.name) {
      return;
    }
    void firstValueFrom(
      this.filterTemplateService.saveFilterTemplate({
        name: event.name,
        documentId: this.dashboardId(),
        autoLoad: event.autoLoad,
        filterValues: event.filterValues,
      })
    )
      .then(() => {
        this.baseline.set(this.selection());
        this.refreshTemplates(event.name, event.autoLoad);
      })
      .catch(error =>
        NotificationUtils.displayNotification(
          this.translocoService.translate(this.templateSaveError, {
            errorDetail: error?.message ?? '',
          }),
          'error',
          {
            closable: true,
            durationInMs: 5000,
          }
        )
      );
  }

  readonly templateSelection = signal<string[]>([]);

  public changeTemplateSelection(options: string[]): void {
    const name = options[0] ?? '';
    const noLabel = this.translocoService.translate(this.noTemplate);
    if (name === '' || name === noLabel) {
      this.applyTemplate('');
      return;
    }
    const template = (this.userfilterTemplates.value() ?? []).find(
      template => this.templateLabel(template) === name
    );
    if (template) {
      this.applyTemplate(template.id);
    }
  }

  private buildSyntheticTemplateFilter(): Filter {
    const noLabel = this.translocoService.translate(this.noTemplate);
    const templates = (this.userfilterTemplates.value() ?? []);
    const options = [
      noLabel,
      ...templates.map(template => this.templateLabel(template))
    ];
    const preselection = this.templateSelection().length ? this.templateSelection() : [noLabel];
    return {
      name: '',
      key: 'template',
      type: FilterType.String,
      available: options,
      preselection,
    };
  }

  readonly templateFilter = computed(() => this.buildSyntheticTemplateFilter());

  public applyTemplate(id: string | null | undefined): void {
    if (id === null || id === undefined || id === '') {
      this.selectedTemplateId.set(undefined);
      this.baseline.set(new Map());
      this.selection.set(new Map());
      this.templateSelection.set([]);
      return;
    }
    const template = (this.userfilterTemplates.value() ?? []).find(template => template.id === id);
    if (!template) {
      return;
    }
    this.selectedTemplateId.set(template.id);
    const templateSelection = this.fromFilterValues(template.filterValues ?? []);
    this.baseline.set(templateSelection);
    this.selection.set(templateSelection);
    this.templateSelection.set([this.templateLabel(template)]);
  }

  public onApplyClick(): void {
    if (this.isTemplateSelected()) {
      this.applyChangesToTemplate();
    } else {
      this.openSaveTemplateDialog();
    }
  }

  public discardOrResetClick(): void {
    if (this.isDirty()) {
      this.discardSelectedChanges();
    } else {
      this.resetAllFilters();
    }
  }

  private applyChangesToTemplate(): void {
    const id = this.selectedTemplateId();
    if (!id) {
      return;
    }
    const template = (this.userfilterTemplates.value() ?? []).find(template => template.id === id);
    if (!template) {
      return;
    }
    void firstValueFrom(
      this.filterTemplateService.saveFilterTemplate({
        id,
        name: template.name,
        documentId: this.dashboardId(),
        autoLoad: template.autoLoad ?? false,
        filterValues: this.activeFilters(),
      })
    )
      .then(() => {
        this.baseline.set(this.selection());
        this.userfilterTemplates.reload();
        NotificationUtils.displayNotification(this.translocoService.translate(this.changesSaved), 'success', {
          closable: true,
          durationInMs: 3000,
        });
      })
      .catch(error =>
        NotificationUtils.displayNotification(
          this.translocoService.translate(this.templateSaveError, {
            errorDetail: error?.message ?? '',
          }),
          'error',
          {
            closable: true,
            durationInMs: 5000,
          }
        )
      );
  }

  public discardSelectedChanges(): void {
    this.selection.set(this.baseline());
  }

  public resetAllFilters(): void {
    this.selectedTemplateId.set(undefined);
    this.baseline.set(new Map());
    this.selection.set(new Map());
  }

  public openDeleteTemplateDialog(): void {
    const component = this.deleteDialog;
    if (!component) {
      return;
    }
    component.open();
  }

  public onTemplateConfirmedDelete(): void {
    const id = this.selectedTemplateId();
    if (!id) {
      return;
    }
    void firstValueFrom(this.filterTemplateService.deleteFilterTemplate(id))
      .then(() => {
        if (id === this.selectedTemplateId()) {
          this.selectedTemplateId.set(undefined);
          this.baseline.set(new Map());
          this.selection.set(new Map());
        }
        this.refreshTemplatesForDelete();
      })
      .catch(error =>
        NotificationUtils.displayNotification(
          this.translocoService.translate(this.templateDeleteError, {
            errorDetail: error?.message ?? '',
          }),
          'error',
          { closable: true, durationInMs: 5000 }
        )
      );
  }

  private fromFilterValues(filterValues: FilterValue[]): Map<string, string[]> {
    const map = new Map<string, string[]>();
    for (const filterValue of filterValues) {
      if (!filterValue?.key) {
        continue;
      }
      map.set(filterValue.key, [...(filterValue.values ?? [])]);
    }
    return map;
  }

  private toFilterValues(selection: Map<string, string[]>): FilterValue[] {
    const result: FilterValue[] = [];
    for (const [key, values] of selection.entries()) {
      if (values.length > 0) {
        result.push({ key, values });
      }
    }
    return result;
  }

  private templateLabel(template: FilterTemplate): string {
    return `${template.name}${template.autoLoad ? ' (Standard)' : ''}`;
  }

  private refreshTemplatesForDelete(): void {
    this.filterTemplateService.loadFilterTemplates().subscribe({
      next: () => {
        this.userfilterTemplates.reload();
        NotificationUtils.displayNotification(
          this.translocoService.translate('dashboard.sidepanel.deleteTemplate.dialog.deleted'),
          'success',
          { closable: true, durationInMs: 3000 }
        );
      },
      error: error =>
        NotificationUtils.displayNotification(
          this.translocoService.translate(this.templateDeleteError, {
            errorDetail: error?.message ?? '',
          }),
          'error',
          { closable: true, durationInMs: 5000 }
        ),
    });
  }

  private refreshTemplates(
    savedName?: string,
    autoLoad = false,
    successKey = 'dashboard.sidepanel.templateSaved'
  ): void {
    this.filterTemplateService.loadFilterTemplates().subscribe({
      next: templates => {
        this.userfilterTemplates.reload();

        if (savedName) {
          this.selectedTemplateId.set(templates.find(template => template.name === savedName)?.id ?? undefined);
        }

        NotificationUtils.displayNotification(this.translocoService.translate(successKey), 'success', {
          closable: true,
          durationInMs: 3000,
        });
      },
      error: error => {
        NotificationUtils.displayNotification(
          this.translocoService.translate(this.templateSaveError, {
            errorDetail: error?.message ?? '',
          }),
          'error',
          {
            closable: true,
            durationInMs: 5000,
          }
        );
      },
    });
  }

  private applyAutoLoadIfNeeded(): void {
    if (this.autoLoadApplied()) {
      return;
    }
    const list = this.userfilterTemplates.value() ?? [];
    const auto = [...list]
      .filter(template => template.autoLoad)
      .sort((leftTemplate, rightTemplate) => (leftTemplate.id ?? '').localeCompare(rightTemplate.id ?? ''))[0];
    if (auto) {
      this.applyTemplate(auto.id);
    }
    this.autoLoadApplied.set(true);
  }

  protected nextTemplateName(): number {
    return (this.userfilterTemplates.value() ?? []).length + 1;
  }

  protected selectedOptions(key: string): string[] {
    return this.selection().get(key) ?? [];
  }

  protected filterKey(filter: Filter): string {
    return filter.key ?? filter.name ?? '';
  }

  protected componentType(filter: Filter): Type {
    if (filter.type === FilterType.Radio) {
      return Type.RADIO_BUTTON;
    }
    return Type.DROPDOWN;
  }

  protected isMultiValue(filter: Filter): boolean {
    return filter.type !== FilterType.Combobox;
  }

  protected toggleOption(key: string, option: string): void {
    const updated = new Map(this.selection());
    const current = new Set(updated.get(key) ?? []);
    if (current.has(option)) {
      current.delete(option);
    } else {
      current.add(option);
    }
    if (current.size === 0) {
      updated.delete(key);
    } else {
      updated.set(key, [...current]);
    }
    this.selection.set(updated);
  }

  protected setSelection(key: string, options: string[]): void {
    const updated = new Map(this.selection());
    if (options.length === 0) {
      updated.delete(key);
    } else {
      updated.set(key, options);
    }
    this.selection.set(updated);
  }

  protected readonly filtersWithPreselection = computed(() => {
    const map = new Map<string, Filter>();
    for (const filter of this.filters.value() ?? []) {
      const key = this.filterKey(filter);
      map.set(key, { ...filter, preselection: this.selectedOptions(key) });
    }
    return map;
  });

  protected readonly iconLibrary = iconLibrary;
}

